import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faChartPie, faGear, faBriefcase,
} from '@fortawesome/free-solid-svg-icons';

import { slugify } from '../utils/gameSlug.js';
import { entitySegment, findBySegment } from '../utils/entityLink.js';

// Portfolios are the signed-in user's own (/v1/web/portfolios), loaded into Redux `userPortfolios` by
// DashboardHeader. Every helper here takes that list rather than reading a module constant, so the
// sidebar, header switcher and pages all follow the same live data.

// Mirrors the game scope: every page is its own top-level section.
export const PORTFOLIO_PAGES = [
    { segment: '', label: 'Dashboard', icon: faChartPie },
    { segment: 'settings', label: 'Settings', icon: faGear },
];

// The colour a new portfolio starts with — the first swatch of the shared ColorPicker.
export const DEFAULT_PORTFOLIO_COLOUR = '--visual-color-1';

const TOKEN = /^--visual-color-\d+$/;
const HEX = /^#[0-9a-f]{3,8}$/i;

// CSS colour for a portfolio. `colour` is stored as the ColorPicker's theme token (so it follows the
// theme); a hex from older data is used as-is; anything else gets the default swatch.
export const portfolioColor = (p) => {
    const c = String(p?.colour || '').trim();
    if (TOKEN.test(c)) return `var(${c})`;
    if (HEX.test(c)) return c;
    return `var(${DEFAULT_PORTFOLIO_COLOUR})`;
};

// The order every portfolio list uses (the API returns them unordered): by name, A→Z.
export const byPortfolioName = (a, b) => String(a?.name || '').localeCompare(String(b?.name || ''));

// The ColorPicker value for a portfolio: its stored token, or the default swatch for anything else.
export const portfolioColourToken = (p) => {
    const c = String(p?.colour || '').trim();
    return TOKEN.test(c) ? c : DEFAULT_PORTFOLIO_COLOUR;
};

// A portfolio's link segment: its name and its id, as for games. The id is what opens it.
export const portfolioSlug = (p) => entitySegment(p?.name, p?.id);

// How portfolio links were written before they carried the id, so an old bookmark still opens.
const legacyPortfolioSlug = (p) => slugify(p?.name).replace(/[/?#%\\]+/g, '') || String(p?.id || '');

// The portfolio a link segment names: by its id, else the first with that old name link (same rule as findGameBySlug).
export const findPortfolioBySlug = (portfolios, slug) => findBySegment(portfolios, slug, legacyPortfolioSlug);

// The portfolio segment of a /portfolio/... path, decoded; null outside one.
export const routePortfolioSlug = (pathname) => {
    const parts = String(pathname || '').split('/').filter(Boolean);
    if (parts[0] !== 'portfolio' || !parts[1]) return null;
    try {
        return decodeURIComponent(parts[1]);
    } catch {
        return parts[1];
    }
};

const toNode = (page, slug, top = false) => ({
    section: top,
    path: `/portfolio/${slug}${page.segment ? `/${page.segment}` : ''}`,
    label: page.label,
    icon: <FontAwesomeIcon icon={page.icon} />,
    exact: page.segment === '',
});

export const portfolioPageLinks = (slug) => PORTFOLIO_PAGES.map(p => toNode(p, slug, true));

export const portfolioListItems = (portfolios) => (Array.isArray(portfolios) ? portfolios : [])
    .filter(p => p && p.id)
    .map(p => ({
        path: `/portfolio/${portfolioSlug(p)}`,
        label: p.name || p.id,
        icon: <FontAwesomeIcon icon={faBriefcase} style={{ color: portfolioColor(p) }} />,
    }));

export const portfolioPageLabel = (segment) =>
    PORTFOLIO_PAGES.find(p => p.segment === segment)?.label || null;
